import { ApolloError } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import { SnackbarProps } from '@mui/material';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import React from 'react';

import Snackbar from './snackbar';

import Button from '~c/components/base/button';
import { parseApolloError } from '~c/helpers/apollo-error';

interface SnackbarOpenOpt {
  /**
   * Show Okay button
   */
  showOk?: boolean;
}

interface SnackbarOpenPayload extends SnackbarOpenOpt {
  /**
   * Message to display in snackbar
   */
  msg: ApolloError | string;
}

type State = Readonly<{
  /**
   * Snackbar in open or closed mode
   */
  open: boolean;
  /**
   * Snackbar message
   */
  message: string;
  /**
   * Show Okay button
   */
  showOk: boolean;
}>;

const initialState: State = {
  open: false,
  message: '',
  showOk: false,
};

const snackbarSlice = createSlice({
  name: 'snackbar',
  initialState,
  reducers: {
    /**
     * Open snackbar with message
     */
    open: {
      prepare: (msg: ApolloError | string, opt?: SnackbarOpenOpt) => {
        return { payload: { msg, ...opt } };
      },
      reducer: (state, { payload }: PayloadAction<SnackbarOpenPayload>) => {
        const result = parseApolloError(payload.msg);
        state.message = result.message ?? 'Error';
        state.showOk = !!payload.showOk;
        state.open = true;
      },
    },
    /**
     * Close snackbar, return to initial state
     */
    close: (state) => initialState,
  },
});

// Helper type to replace the return type of action creator
type ReplaceReturnType<T extends (...a: any) => any, TNewReturn> = (
  ...a: Parameters<T>
) => TNewReturn;

const { actions } = snackbarSlice;

interface ContextT {
  state: State;
  // The following actions are safe to be used from appContext directly
  open: ReplaceReturnType<typeof actions.open, void>;
}

// @ts-expect-error: intentionally leaving default value to be empty
const Context = React.createContext<ContextT>();

interface Props {
  children: React.ReactNode;
}

export function SnackbarContextProvider({ children, ...props }: Props) {
  const [state, dispatch] = React.useReducer(
    snackbarSlice.reducer,
    initialState
  );

  const closeSnackbar = () => dispatch(actions.close());
  const actionList: Omit<ContextT, 'state' | 'close'> = {
    open: (str, opt) => dispatch(actions.open(str, opt)),
  };

  const onClose: SnackbarProps['onClose'] = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    closeSnackbar();
  };

  return (
    <Context.Provider value={{ state, ...actionList }} {...props}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={6000}
        onClose={onClose}
        message={state.message}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        action={
          <Button
            onClick={closeSnackbar}
            {...(!state.showOk && { icon: CloseIcon })}
            {...(!!state.showOk && { children: 'Okay' })}
          />
        }
      />
    </Context.Provider>
  );
}

export default function useSnackbarContext() {
  const context = React.useContext(Context);
  if (!context) {
    throw new Error(
      `useSnackbarContext must be used within a SnackbarContextProvider`
    );
  }
  return context;
}
